import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Wordmark } from "../components/Wordmark";
import { api, taxDeletePhrase, type Capture } from "../lib/api";
import {
  eventLink,
  eventLocation,
  eventWhen,
  iCloudDeeplink,
  linkParts,
  noteTitle,
  reminderWhen,
  taskText,
} from "../lib/derive";
import { fullTimestamp, relTime } from "../lib/format";
import { CATEGORY_META, Icon } from "../lib/icons";
import { renderMarkdown } from "../lib/markdown";

export function CaptureDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const capture = useQuery({
    queryKey: ["capture", id],
    queryFn: () => api.getCapture(Number(id)),
    enabled: !!id,
  });

  if (capture.isLoading) {
    return (
      <main className="col" style={{ paddingTop: 80 }}>
        <p style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading…</p>
      </main>
    );
  }

  if (capture.isError || !capture.data) {
    return (
      <>
        <DetailHeader onBack={() => navigate("/")} />
        <main className="detail">
          <div className="detail-col">
            <div className="empty">
              <h2 className="empty-title">This capture is no longer in the archive.</h2>
            </div>
          </div>
        </main>
      </>
    );
  }

  const c = capture.data;
  const meta = CATEGORY_META[c.category];
  const isTax = c.category === "tax";

  return (
    <>
      <DetailHeader onBack={() => navigate("/")} capture={c} />
      <main className="detail">
        <div className="detail-col" style={{ ["--cat-color" as string]: meta?.color }}>
          <div className="detail-eyebrow">
            <span className="dot" />
            <span>{meta?.label ?? c.category}</span>
            <span className="crumb">· {fullTimestamp(c.captured_at).split("·")[0].trim()}</span>
          </div>
          {c.category === "note" && <NoteView c={c} />}
          {c.category === "link" && <LinkView c={c} />}
          {c.category === "event" && <EventView c={c} />}
          {c.category === "tax" && <TaxView c={c} />}
          {(c.category === "task" || c.category === "reminder") && <TaskView c={c} />}
          {!["note", "link", "event", "task", "reminder", "tax"].includes(c.category) && (
            <GenericView c={c} />
          )}
          <ScheduleControls c={c} />
          <CompleteControls c={c} />
          {/* Tax records are immutable — no pin/archive flags. */}
          {!isTax && <FlagControls c={c} />}
        </div>
      </main>
    </>
  );
}

function DetailHeader({ onBack, capture }: { onBack: () => void; capture?: Capture }) {
  return (
    <header className="header">
      <div className="col">
        <div className="header-inner">
          <button className="back-link" onClick={onBack}>
            <Icon.back /> back
          </button>
          <div style={{ flex: 1 }} />
          {capture != null ? (
            capture.category === "tax" ? (
              <ForceDeleteAction capture={capture} />
            ) : (
              <DeleteAction id={capture.id} />
            )
          ) : null}
          <Wordmark />
        </div>
      </div>
    </header>
  );
}

// Immutable-record removal: a trash button that opens a modal requiring the
// exact confirmation phrase. No one-tap delete — friction proportional to the
// record's permanence (see force_delete_tax / TAX.md §C1).
function ForceDeleteAction({ capture }: { capture: Capture }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const phrase = taxDeletePhrase(capture);

  const doDelete = async () => {
    setErr(null);
    try {
      await api.forceDeleteCapture(capture.id, text.trim());
      qc.invalidateQueries({ queryKey: ["captures"] });
      qc.invalidateQueries({ queryKey: ["categories"] });
      navigate("/tax", { replace: true });
    } catch {
      setErr("That phrase didn’t match — nothing was deleted.");
    }
  };

  return (
    <>
      <button
        className="detail-delete"
        onClick={() => {
          setText("");
          setErr(null);
          setOpen(true);
        }}
        aria-label="Delete tax record"
      >
        <Icon.trash />
      </button>
      {open ? (
        <div className="force-modal-overlay" onClick={() => setOpen(false)}>
          <div className="force-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Delete tax record</h2>
            <p>
              Tax records are immutable. To delete this one for good, type{" "}
              <code>{phrase}</code> below.
            </p>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
              placeholder={phrase}
            />
            {err ? <p className="force-modal-error">{err}</p> : null}
            <div className="force-modal-actions">
              <button className="force-modal-cancel" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                className="force-modal-delete"
                disabled={text.trim() !== phrase}
                onClick={doDelete}
              >
                Delete forever
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

// Two-step delete on the detail page: a trash button that reveals an inline
// confirm. On confirm it removes the capture and returns to the timeline.
function DeleteAction({ id }: { id: number }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const doDelete = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await api.deleteCapture(id);
      qc.invalidateQueries({ queryKey: ["captures"] });
      qc.invalidateQueries({ queryKey: ["categories"] });
      navigate("/", { replace: true });
    } catch {
      setBusy(false);
      setFailed(true);
    }
  };

  if (!confirming) {
    return (
      <button
        className="detail-delete"
        onClick={() => setConfirming(true)}
        aria-label="Delete capture"
      >
        <Icon.trash />
      </button>
    );
  }
  return (
    <span className="detail-delete-confirm">
      <span>{failed ? "Couldn’t delete" : "Delete?"}</span>
      <button className="confirm-yes" disabled={busy} onClick={doDelete}>
        {busy ? "…" : "Delete"}
      </button>
      <button
        className="confirm-no"
        disabled={busy}
        onClick={() => {
          setConfirming(false);
          setFailed(false);
        }}
      >
        Cancel
      </button>
    </span>
  );
}

function MetaTags({ c }: { c: Capture }) {
  const navigate = useNavigate();
  if (!c.tags.length) return null;
  return (
    <>
      <span className="sep" />
      <span className="row-tags">
        {c.tags.map((t) => (
          <button
            key={t}
            className="tag tag-btn"
            onClick={() => navigate(`/?tag=${encodeURIComponent(t)}`)}
          >
            {t}
          </button>
        ))}
      </span>
    </>
  );
}

// The note's image (if any) is served by the API behind the session cookie.
// We always attempt it and hide on error, so notes without an image (or whose
// attachment can't render, e.g. a PDF) simply show nothing.
function NoteImage({ id }: { id: number }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      className="note-image"
      src={`/api/captures/${id}/image`}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

// Notes are editable in place (title/body/tags) — an explicit owner action that
// rewrites the .md + the index row (see PATCH /api/captures/{id}).
function NoteView({ c }: { c: Capture }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const startEdit = () => {
    setTitle(noteTitle(c));
    setBody(c.body || "");
    setTags((c.tags || []).join(", "));
    setErr(null);
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.editCapture(c.id, {
        title: title.trim(),
        body,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      });
      qc.invalidateQueries({ queryKey: ["capture"] });
      qc.invalidateQueries({ queryKey: ["captures"] });
      setEditing(false);
    } catch {
      setErr("Couldn’t save — try again.");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <article className="fade-up">
        <input
          className="edit-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
        />
        <textarea
          className="edit-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={10}
          placeholder="Note…"
        />
        <input
          className="edit-tags"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="tags, comma, separated"
        />
        {err ? <p className="edit-error">{err}</p> : null}
        <div className="edit-actions">
          <button className="btn-save" disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button className="btn-cancel" disabled={busy} onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </article>
    );
  }

  return (
    <article className="fade-up">
      <h1 className="detail-title">{noteTitle(c)}</h1>
      <div className="detail-meta">
        <span>{fullTimestamp(c.captured_at)}</span>
        <MetaTags c={c} />
        <button className="meta-edit" onClick={startEdit}>
          Edit
        </button>
      </div>
      <NoteImage id={c.id} />
      <div className="note-body">{renderMarkdown(c.body || "")}</div>
    </article>
  );
}

function LinkView({ c }: { c: Capture }) {
  const parts = linkParts(c);
  return (
    <article className="fade-up">
      <h1 className="detail-title">{parts.title}</h1>
      <div className="detail-meta">
        <span>
          Saved {relTime(c.captured_at)} ·{" "}
          {fullTimestamp(c.captured_at).split("·")[0].trim()}
        </span>
        <MetaTags c={c} />
      </div>

      {parts.url ? (
        <div className="link-source">
          <span className="favicon">{parts.favicon}</span>
          <div className="meta">
            <div className="host">{parts.host}</div>
            <span className="url">{parts.url.replace(/^https?:\/\//, "")}</span>
          </div>
          <a className="btn-open" href={parts.url} target="_blank" rel="noopener noreferrer">
            Open original <Icon.external />
          </a>
        </div>
      ) : null}

      {parts.userComment ? (
        <blockquote className="key-quote">{parts.userComment}</blockquote>
      ) : null}

      {parts.tldr ? <p className="tldr">{parts.tldr}</p> : null}

      {parts.bullets.length > 0 ? (
        <>
          <p className="detail-section-label">Summary</p>
          <ul className="bullets">
            {parts.bullets.map((b, i) => (
              <li key={i}>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </>
      ) : parts.rest ? (
        <div className="note-body">{renderMarkdown(parts.rest)}</div>
      ) : null}
    </article>
  );
}

function TaskView({ c }: { c: Capture }) {
  const isReminder = c.category === "reminder";
  const when = isReminder ? reminderWhen(c) : null;
  const deeplink = iCloudDeeplink(c);
  return (
    <article className="fade-up">
      <p className="task-text">{taskText(c)}</p>
      {when ? (
        <div className="task-when">
          <Icon.clock />
          <span className="label">{isReminder ? "remind me" : "due"}</span>
          <span>{when}</span>
        </div>
      ) : null}
      {deeplink ? (
        <a className="deeplink" href={deeplink}>
          <Icon.apple />
          Open in Reminders
        </a>
      ) : null}
    </article>
  );
}

function EventView({ c }: { c: Capture }) {
  const when = eventWhen(c);
  const location = eventLocation(c);
  const link = eventLink(c);
  return (
    <article className="fade-up">
      <h1 className="detail-title">{noteTitle(c)}</h1>
      <div className="detail-meta">
        <span>{fullTimestamp(c.captured_at)}</span>
        <MetaTags c={c} />
      </div>
      <NoteImage id={c.id} />
      {when ? (
        <div className="task-when">
          <Icon.event />
          <span className="label">when</span>
          <span>{when}</span>
        </div>
      ) : null}
      {location ? (
        <div className="task-when">
          <span className="label">where</span>
          <span>{location}</span>
        </div>
      ) : null}
      {link ? (
        <a className="deeplink" href={link} target="_blank" rel="noopener noreferrer">
          <Icon.external />
          Open in Google Calendar
        </a>
      ) : null}
    </article>
  );
}

// Tax records are immutable, year-stamped receipts. Render the OCR'd fields as
// a labelled list plus the bill image; no edit affordance (the field list is
// read-only and editing is blocked at the API anyway).
function TaxView({ c }: { c: Capture }) {
  const p = (c.payload ?? {}) as Record<string, unknown>;
  const rows: [string, unknown][] = [
    ["Vendor", p.vendor],
    ["Amount", p.amount],
    ["Bill date", p.bill_date],
    ["Tax year", p.year],
    ["Note", p.note],
  ];
  const shown = rows.filter(([, v]) => v !== undefined && v !== null && v !== "");
  return (
    <article className="fade-up">
      <h1 className="detail-title">{String(p.vendor ?? c.summary)}</h1>
      <div className="detail-meta">
        <span>{fullTimestamp(c.captured_at)}</span>
        <MetaTags c={c} />
      </div>
      <dl className="tax-fields">
        {shown.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{String(value)}</dd>
          </div>
        ))}
      </dl>
      <NoteImage id={c.id} />
      <p className="tax-immutable-note">
        Immutable record. Removal requires a typed confirmation (the trash button above).
      </p>
    </article>
  );
}

// Fallback detail view for categories without a bespoke renderer (e.g.
// shopping) — title + the captured body.
function GenericView({ c }: { c: Capture }) {
  return (
    <article className="fade-up">
      <h1 className="detail-title">{noteTitle(c)}</h1>
      <div className="detail-meta">
        <span>{fullTimestamp(c.captured_at)}</span>
        <MetaTags c={c} />
      </div>
      {c.body ? <div className="note-body">{renderMarkdown(c.body)}</div> : null}
    </article>
  );
}

// Manage a scheduled proactive delivery (events/reminders carrying an
// attachment). Self-hides when the capture has no schedule. Snooze/reschedule
// re-arms (clears the sent marker server-side); cancel stops it.
function ScheduleControls({ c }: { c: Capture }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!c.surface_at && !c.surfaced_at) return null;

  const set = async (surfaceAt: string | null) => {
    setBusy(true);
    setErr(null);
    try {
      await api.scheduleCapture(c.id, surfaceAt);
      qc.invalidateQueries({ queryKey: ["capture"] });
      qc.invalidateQueries({ queryKey: ["captures"] });
    } catch {
      setErr("Couldn’t update the schedule — try again.");
    } finally {
      setBusy(false);
    }
  };
  const inHours = (h: number) => set(new Date(Date.now() + h * 3600000).toISOString());

  const status = c.surfaced_at
    ? `Delivered ${relTime(c.surfaced_at)}`
    : c.surface_at
      ? `Scheduled · ${fullTimestamp(c.surface_at)}`
      : "Delivery cancelled";

  return (
    <div className="schedule-panel">
      <div className="schedule-status">
        <Icon.clock />
        <span>{status}</span>
      </div>
      <div className="schedule-actions">
        <button disabled={busy} onClick={() => set(new Date().toISOString())}>
          Send now
        </button>
        <button disabled={busy} onClick={() => inHours(1)}>
          Snooze 1h
        </button>
        <button disabled={busy} onClick={() => inHours(24)}>
          Snooze 1d
        </button>
        {c.surface_at ? (
          <button disabled={busy} onClick={() => set(null)}>
            Cancel
          </button>
        ) : null}
      </div>
      {err ? <p className="edit-error">{err}</p> : null}
    </div>
  );
}

// Mark a reminder/task/shopping done on its CalDAV list (irreversible from the
// app). Self-hides for other categories; shows a badge once completed.
function CompleteControls({ c }: { c: Capture }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!["reminder", "task", "shopping"].includes(c.category)) return null;
  if ((c.payload as Record<string, unknown>)?.completed === true) {
    return <p className="completed-badge">✓ Completed</p>;
  }
  const complete = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.completeCapture(c.id);
      qc.invalidateQueries({ queryKey: ["capture"] });
      qc.invalidateQueries({ queryKey: ["captures"] });
    } catch {
      setErr("Couldn’t complete — try again.");
      setBusy(false);
    }
  };
  return (
    <div className="complete-row">
      <button className="btn-complete" disabled={busy} onClick={complete}>
        {busy ? "…" : "✓ Mark done"}
      </button>
      {err ? <span className="edit-error">{err}</span> : null}
    </div>
  );
}

// Pin (float to top of the timeline) / archive (hide from it) — explicit owner
// toggles. Archived items still surface in search/Ask; they just leave the feed.
function FlagControls({ c }: { c: Capture }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pinned = c.pinned === 1;
  const archived = c.archived === 1;
  const set = async (flags: { pinned?: boolean; archived?: boolean }) => {
    setBusy(true);
    setErr(null);
    try {
      await api.setFlags(c.id, flags);
      qc.invalidateQueries({ queryKey: ["capture"] });
      qc.invalidateQueries({ queryKey: ["captures"] });
      // Archiving moves a capture out of (or back into) the category counts.
      qc.invalidateQueries({ queryKey: ["categories"] });
    } catch {
      setErr("Couldn’t update — try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="flag-row">
        <button disabled={busy} onClick={() => set({ pinned: !pinned })}>
          {pinned ? "📌 Unpin" : "📌 Pin"}
        </button>
        <button disabled={busy} onClick={() => set({ archived: !archived })}>
          {archived ? "Unarchive" : "Archive"}
        </button>
      </div>
      {err ? <p className="edit-error">{err}</p> : null}
    </>
  );
}
